import React, { useCallback, useEffect, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { getProviderAutocomplete, getProviderId } from 'features/salesOrder/services';
import { getProviderList } from 'features/providers/services';

export type ProviderOption = {
	id: string;
	fantasyName: string;
	businessName: string;
	stateName: string;
};

interface BulkProviderAutocompleteProps {
	value: string;
	onChange: (providerId: string) => void;
	label?: string;
	agencyId?: number;
	helperText?: string;
}

const mapAutocompleteOption = (option: {
	id: string | number;
	fantasyName: string;
	businessName: string;
	stateName?: string;
}): ProviderOption => ({
	id: String(option.id),
	fantasyName: option.fantasyName,
	businessName: option.businessName,
	stateName: option.stateName ?? '',
});

const getOptionLabel = (option: ProviderOption): string => `${option.fantasyName} (${option.businessName})`;

export const BulkProviderAutocomplete = ({
	value,
	onChange,
	label = 'Nuevo proveedor de tarifa',
	agencyId,
	helperText = 'Elegí de la lista o escribí al menos 4 caracteres para buscar',
}: BulkProviderAutocompleteProps): JSX.Element => {
	const [selected, setSelected] = useState<ProviderOption | null>(null);
	const [options, setOptions] = useState<ProviderOption[]>([]);
	const [inputValue, setInputValue] = useState('');
	const [loading, setLoading] = useState(false);
	const debouncer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const initialLoadedRef = useRef(false);

	const loadInitialOptions = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await getProviderList(1, 100, { enable: true }, '', undefined, undefined, agencyId);
			const mapped = (data.elements ?? []).map((provider) =>
				mapAutocompleteOption({
					id: provider.id,
					fantasyName: provider.fantasyName,
					businessName: provider.businessName,
					stateName: provider.stateName,
				}),
			);
			setOptions(mapped);
			initialLoadedRef.current = true;
		} catch {
			setOptions([]);
		} finally {
			setLoading(false);
		}
	}, [agencyId]);

	useEffect(() => {
		initialLoadedRef.current = false;
		void loadInitialOptions();
	}, [loadInitialOptions]);

	useEffect(() => {
		if (!value) {
			setSelected(null);
			return;
		}

		const existing = options.find((option) => option.id === value);
		if (existing) {
			setSelected(existing);
			setInputValue(getOptionLabel(existing));
			return;
		}

		let cancelled = false;
		getProviderId(value, agencyId)
			.then((result) => {
				if (cancelled || !result.data) return;
				const opt = mapAutocompleteOption({
					id: result.data.id,
					fantasyName: result.data.fantasyName,
					businessName: result.data.businessName,
					stateName: result.data.stateName,
				});
				setSelected(opt);
				setInputValue(getOptionLabel(opt));
				setOptions((prev) => (prev.some((item) => item.id === opt.id) ? prev : [opt, ...prev]));
			})
			.catch(() => {
				if (!cancelled) setInputValue(value);
			});

		return () => {
			cancelled = true;
		};
	}, [value, options, agencyId]);

	const searchProviders = useCallback(
		async (query: string) => {
			const trimmed = query.trim();
			if (trimmed.length < 4) {
				if (!initialLoadedRef.current) {
					await loadInitialOptions();
				}
				return;
			}

			setLoading(true);
			try {
				const response = await getProviderAutocomplete(trimmed, agencyId);
				setOptions(response.data.map(mapAutocompleteOption));
			} catch {
				setOptions([]);
			} finally {
				setLoading(false);
			}
		},
		[loadInitialOptions, agencyId],
	);

	const handleInputChange = (_: unknown, newInput: string) => {
		setInputValue(newInput);
		if (debouncer.current) {
			clearTimeout(debouncer.current);
		}
		debouncer.current = setTimeout(() => {
			void searchProviders(newInput);
		}, 300);
	};

	const handleOpen = () => {
		if (!options.length) {
			void loadInitialOptions();
		}
	};

	return (
		<Autocomplete
			options={options}
			value={selected}
			inputValue={inputValue}
			loading={loading}
			onOpen={handleOpen}
			onInputChange={handleInputChange}
			getOptionLabel={getOptionLabel}
			isOptionEqualToValue={(a, b) => a.id === b.id}
			noOptionsText={loading ? 'Cargando...' : 'Sin resultados. Probá con otro término.'}
			onChange={(_, newValue) => {
				setSelected(newValue);
				onChange(newValue?.id ?? '');
				if (newValue) {
					setInputValue(getOptionLabel(newValue));
				}
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					size="small"
					fullWidth
					helperText={helperText}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{loading ? <CircularProgress color="inherit" size={18} /> : null}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			)}
		/>
	);
};
